import { Heart, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import type { KarmaUser } from "@/lib/karma/types";
import { computeKarmaNumerology } from "@/lib/karma/numerology";
import { computeArchetype } from "@/lib/karma/archetypes";
import { getDestinyTheme, themedPanelClass } from "@/lib/karma/theme";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

function getPrimaryPhoto(user: KarmaUser) {
  return user.photos[0]?.url || "/placeholder.svg";
}

export function UserCard({
  user,
  onLike,
  className,
  showMatchBadge,
}: {
  user: KarmaUser;
  onLike?: () => void;
  className?: string;
  showMatchBadge?: boolean;
}) {
  const numerology = computeKarmaNumerology(user.displayName, user.birthDate);
  const archetype = computeArchetype(numerology);
  const theme = getDestinyTheme(numerology.destinyNumber);
  const photo = getPrimaryPhoto(user);

  return (
    <div className={themedPanelClass(numerology.destinyNumber, className)}>
      <div
        className={cn(
          "pointer-events-none absolute inset-0 bg-gradient-to-br",
          theme.gradient,
        )}
      />

      <div className="relative flex gap-4 p-4">
        <div className="h-16 w-16 overflow-hidden rounded-2xl border bg-muted">
          <img
            src={photo}
            alt={user.displayName}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <Link
                to={`/karma/u/${user.handle}`}
                className="block truncate font-display text-lg font-semibold tracking-tight hover:underline"
              >
                {user.displayName}
              </Link>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                    <span
                      className={cn(
                        "grid h-6 w-6 place-items-center rounded-full text-[0.75rem] font-semibold",
                        theme.badge,
                      )}
                    >
                      {numerology.destinyNumber}
                    </span>
                    <span>
                      Kader{numerology.lifePathMaster ? ` (${numerology.lifePathMaster}→${numerology.destinyNumber})` : ""}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-primary text-[0.75rem] font-semibold text-primary-foreground">
                      {numerology.heartDesireNumber}
                    </span>
                    <span>Kalp</span>
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-secondary text-[0.75rem] font-semibold text-secondary-foreground">
                      {numerology.expressionNumber}
                    </span>
                    <span>İfade</span>
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                    <span className="grid h-6 w-6 place-items-center rounded-full bg-primary/10 text-[0.75rem] font-semibold text-primary">
                      {archetype.digit}
                    </span>
                    <span>Arketip {archetype.name}</span>
                  </span>
                </div>
                {showMatchBadge && user.matchEnabled ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    <Sparkles className="h-3.5 w-3.5" />
                    Eşleşmeye açık
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{user.likes}</span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={onLike}
                className="h-9 w-9 rounded-full"
                aria-label="Beğen"
              >
                <Heart className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <p className="line-clamp-2 text-sm text-muted-foreground">
            {user.bio || "Kendini tanıt ve enerjini paylaş."}
          </p>
        </div>
      </div>
    </div>
  );
}
